import { useMemo } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { mapFileTypeToFileExtensions } from "../components/dropdowns/fileTypeDropdown";
import {
  ADDENDUM_SUFFIX,
  ARCHIVE_SUFFIX,
  PAGE_SIZE,
} from "../components/dataBrowse/constants";
import {
  mapDirectoriesToFileItems,
  mapFilesToFileItems,
  manifestTypesForPath,
  parentPathForApi,
} from "../components/dataBrowse/utils";
import { AdvancedSearchParams } from "../components/dataBrowse/types";
import useApiClient from "./useApiClient";
import { isSearchQueryTooShort } from "@/utils/searchQueryValidation";
import {
  resolveDateRange,
  toUtcStartOfDay,
  toUtcStartOfNextDay,
} from "../utils/dateRanges";

type UseDataBrowseQueriesArgs = {
  currentPath: string[];
  activeFilters: AdvancedSearchParams | null;
  debouncedSearchQuery: string;
  realAssetDeploymentPublicId?: string;
};

const useDataBrowseQueries = ({
  currentPath,
  activeFilters,
  debouncedSearchQuery,
  realAssetDeploymentPublicId,
}: UseDataBrowseQueriesArgs) => {
  const api = useApiClient();
  const currentCruiseId = currentPath[0];
  const currentDeploymentPublicId = realAssetDeploymentPublicId;
  const parentPath = useMemo(
    () => parentPathForApi(currentPath),
    [currentPath]
  );
  // The API rejects search terms under the 3-character index floor (422);
  // treat a too-short query as no query so browsing falls back to listing.
  const searchQueryTooShort = isSearchQueryTooShort(debouncedSearchQuery);
  const effectiveSearchQuery = searchQueryTooShort ? "" : debouncedSearchQuery;
  // directoryParam = non-recursive listing for browsing; parentPrefixParam = recursive search within the current folder
  const directoryParam = useMemo(() => parentPath ?? "", [parentPath]);
  const manifestTypes = useMemo(
    () => manifestTypesForPath(currentPath),
    [currentPath]
  );
  const manifestSegment = currentPath[1] || "";
  const isArchive = currentPath[1]?.includes(ARCHIVE_SUFFIX);
  const isAddendum = currentPath[1]?.includes(ADDENDUM_SUFFIX);
  const fileExtensions = useMemo(
    () =>
      activeFilters?.fileTypes.length
        ? (mapFileTypeToFileExtensions(activeFilters.fileTypes)[1] as string[])
        : undefined,
    [activeFilters]
  );
  const hasRecursiveFilters = useMemo(() => {
    if (!activeFilters) return false;
    const hasFileTypes = activeFilters.fileTypes.length > 0;
    const hasDateFilter =
      activeFilters.dateModified !== "any" ||
      !!activeFilters.dateFrom ||
      !!activeFilters.dateTo;
    return hasFileTypes || hasDateFilter;
  }, [activeFilters]);
  const shouldSearchRecursively = useMemo(
    () => !!effectiveSearchQuery || hasRecursiveFilters,
    [effectiveSearchQuery, hasRecursiveFilters]
  );
  const parentPrefixParam = useMemo(
    () => (shouldSearchRecursively ? directoryParam || undefined : undefined),
    [shouldSearchRecursively, directoryParam]
  );
  const directoryFilterParam = useMemo(
    () => (!shouldSearchRecursively ? directoryParam : undefined),
    [shouldSearchRecursively, directoryParam]
  );
  const { assetModifiedAfter, assetModifiedBefore, dateFilterKey } =
    useMemo(() => {
      if (!activeFilters) {
        return {
          assetModifiedAfter: undefined,
          assetModifiedBefore: undefined,
          dateFilterKey: "",
        };
      }

      const { startDate, endDate } = resolveDateRange(activeFilters);

      const afterIso = toUtcStartOfDay(startDate);
      const beforeIso = toUtcStartOfNextDay(endDate);
      return {
        assetModifiedAfter: afterIso,
        assetModifiedBefore: beforeIso,
        dateFilterKey: `${afterIso || ""}|${beforeIso || ""}`,
      };
    }, [activeFilters]);
  const fileExtensionsKey = useMemo(
    () => fileExtensions?.join(",") || "",
    [fileExtensions]
  );

  const directoriesQuery = useInfiniteQuery({
    queryKey: [
      "data-asset-tree-directories",
      currentDeploymentPublicId,
      manifestSegment,
      directoryParam,
      manifestTypes,
      effectiveSearchQuery,
      dateFilterKey,
      shouldSearchRecursively,
    ],
    queryFn: ({ pageParam = 1 }) =>
      api.dataAccess.listTreeDirectories({
        realAssetDeploymentPublicId: currentDeploymentPublicId as string,
        directory: directoryParam,
        manifestTypes,
        searchTerm: effectiveSearchQuery || undefined,
        recursive: shouldSearchRecursively,
        assetModifiedAfter,
        assetModifiedBefore,
        page: pageParam,
        size: PAGE_SIZE,
      }),
    initialPageParam: 1,
    enabled: currentPath.length >= 2 && !!currentDeploymentPublicId,
    refetchOnWindowFocus: false,
    getNextPageParam: (lastPage, pages) =>
      (lastPage.items || []).length === PAGE_SIZE
        ? pages.length + 1
        : undefined,
  });

  const filesQuery = useInfiniteQuery({
    queryKey: [
      "data-asset-tree-files",
      currentDeploymentPublicId,
      manifestSegment,
      directoryFilterParam,
      parentPrefixParam,
      manifestTypes,
      effectiveSearchQuery,
      fileExtensionsKey,
      dateFilterKey,
    ],
    queryFn: ({ pageParam = 1 }) =>
      api.dataAccess.searchDataAssets({
        realAssetDeploymentPublicIds: currentDeploymentPublicId
          ? [currentDeploymentPublicId]
          : undefined,
        directory: directoryFilterParam,
        parentPrefix: parentPrefixParam,
        manifestTypes,
        query: effectiveSearchQuery || undefined,
        fileExtensions,
        assetModifiedAfter,
        assetModifiedBefore,
        includeDescendantDeployments: true,
        squashByPath: !!isAddendum,
        page: pageParam,
        size: PAGE_SIZE,
      }),
    initialPageParam: 1,
    enabled: currentPath.length >= 2 && !!currentDeploymentPublicId,
    refetchOnWindowFocus: false,
    getNextPageParam: (lastPage, pages) =>
      (lastPage.items || []).length === PAGE_SIZE
        ? pages.length + 1
        : undefined,
  });

  const directoryItems = useMemo(() => {
    if (!directoriesQuery.data) return [];
    return directoriesQuery.data.pages.flatMap((page) =>
      mapDirectoriesToFileItems(page.items || [], currentPath, !!isArchive)
    );
  }, [directoriesQuery.data, currentPath, isArchive]);

  const fileItems = useMemo(() => {
    if (!filesQuery.data) return [];
    return filesQuery.data.pages.flatMap((page) =>
      mapFilesToFileItems(page.items || [], currentPath, !!isArchive)
    );
  }, [filesQuery.data, currentPath, isArchive]);

  const folderItems = useMemo(
    () => [...directoryItems, ...fileItems],
    [directoryItems, fileItems]
  );

  const isFetchingNodes =
    currentPath.length >= 2 &&
    (directoriesQuery.isFetching || filesQuery.isFetching);
  const isLoadingMore =
    currentPath.length >= 2 &&
    (directoriesQuery.isFetchingNextPage || filesQuery.isFetchingNextPage);

  return {
    currentCruiseId,
    currentDeploymentPublicId,
    parentPath,
    manifestTypes,
    manifestSegment,
    isArchive,
    fileExtensions,
    fileExtensionsKey,
    directoriesQuery,
    filesQuery,
    directoryItems,
    fileItems,
    folderItems,
    isFetchingNodes,
    isLoadingMore,
  };
};

export default useDataBrowseQueries;
